import { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Pause, Play, Trash2, Download, Smartphone, Users, AlertTriangle, RefreshCw, Copy, Check } from 'lucide-react';
import { useDevices } from '@/hooks/useDevices';
import { useRealtimeDevices } from '@/hooks/useRealtimeDevices';
import { useRealtimeLogs } from '@/hooks/useRealtimeLogs';
import { useDashboardMetrics } from '@/hooks/useDashboard';
import { useExportLogs } from '@/hooks/useLogs';
import type { LogLevel } from '@/types/log.types';
import { formatDateTime } from '@/utils/formatters';

const LOG_COLORS = {
  DEBUG: '#2196F3',
  INFO: '#4CAF50',
  WARNING: '#FF9800',
  ERROR: '#FE5963',
  FATAL: '#9C27B0',
};

const getLogColor = (level: string) => LOG_COLORS[level as keyof typeof LOG_COLORS] || '#94A3B8';

export function Logs() {
  useRealtimeDevices();

  const [searchParams] = useSearchParams();
  const deviceIdFromUrl = searchParams.get('device');
  const logContainerRef = useRef<HTMLDivElement>(null);

  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(deviceIdFromUrl);
  const [selectedLevel, setSelectedLevel] = useState<LogLevel | 'all'>('all');
  const [autoScroll, setAutoScroll] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [copiedAll, setCopiedAll] = useState(false);

  const { data: devicesData, isLoading: devicesLoading } = useDevices();
  const { data: metricsData, isLoading: metricsLoading } = useDashboardMetrics();
  const { logs, isConnected, clearLogs } = useRealtimeLogs(500);
  const { mutate: exportLogs, isPending: isExporting } = useExportLogs();

  const onlineDevices = (devicesData?.devices || []).filter(d => d.status === 'online');
  const selectedDevice = onlineDevices.find(d => d.id === selectedDeviceId);

  useEffect(() => {
    if (deviceIdFromUrl && !selectedDeviceId) {
      setSelectedDeviceId(deviceIdFromUrl);
    }
  }, [deviceIdFromUrl, selectedDeviceId]);

  const filteredLogs = useMemo(() => {
    const fingerprint = selectedDevice?.fingerprint?.toLowerCase();

    return logs.filter((log) => {
      if (selectedLevel !== 'all' && log.level !== selectedLevel) return false;
      if (!selectedDevice) return true;
      // FIXME: API não está retornando `fingerprint` em GET /devices.
      // Sem ele, não dá para filtrar por dispositivo — mostra todos os logs.
      if (!fingerprint) return true;
      return log.deviceId?.toLowerCase() === fingerprint;
    });
  }, [logs, selectedLevel, selectedDevice]);

  useEffect(() => {
    if (!autoScroll || isPaused) return;
    const el = logContainerRef.current;
    if (!el) return;
    const id = requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
    return () => cancelAnimationFrame(id);
  }, [filteredLogs, autoScroll, isPaused]);

  const handleExportLogs = () => {
    exportLogs({
      deviceId: selectedDeviceId || undefined,
      level: selectedLevel !== 'all' ? selectedLevel : undefined,
    });
  };

  const handleClearLogs = () => {
    if (confirm('Tem certeza que deseja limpar o console?')) {
      clearLogs();
    }
  };

  const handleCopyAllLogs = async () => {
    if (filteredLogs.length === 0) {
      alert('Não há logs para copiar');
      return;
    }

    const logsText = filteredLogs
      .map(log => `${log.timestamp} [${log.level}] [${log.component}] ${log.message}`)
      .join('\n');

    const header = `=== LOGS DO SISTEMA ===\n` +
      `Dispositivo: ${selectedDevice?.userName || 'N/A'} (${selectedDevice?.model || 'N/A'})\n` +
      `Filtro: ${selectedLevel !== 'all' ? selectedLevel : 'Todos os níveis'}\n` +
      `Total de logs: ${filteredLogs.length}\n` +
      `Data de exportação: ${formatDateTime(new Date())}\n` +
      `\n${'-'.repeat(80)}\n\n`;

    const fullText = header + logsText;

    try {
      await navigator.clipboard.writeText(fullText);
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2000);
    } catch (err) {
      console.error('Failed to copy logs:', err);
      alert('Erro ao copiar logs. Tente novamente.');
    }
  };

  return (
    <Layout breadcrumbs={[{ label: 'Home', path: '/' }, { label: 'System Logs' }]}>
      <div className="flex flex-col gap-4 p-3 sm:p-4 lg:gap-6 lg:p-6">
        {/* Metrics Card */}
        <div className="rounded-lg border border-border bg-card p-4 shadow-sm lg:p-6">
          <h2 className="mb-4 text-xl font-semibold text-text-title lg:mb-6 lg:text-2xl">Monitoramento do Sistema</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4 lg:gap-6">
            <div className="flex items-center gap-4 rounded-lg border border-border p-4">
              <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-online/10 text-online">
                <Smartphone size={22} />
              </div>
              <div className="min-w-0">
                <p className="text-sm text-text-secondary">Dispositivos Online</p>
                <p className="text-2xl font-bold tabular-nums text-text-title">
                  {devicesLoading ? '—' : onlineDevices.length}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4 rounded-lg border border-border p-4">
              <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-info/10 text-info">
                <Users size={22} />
              </div>
              <div className="min-w-0">
                <p className="text-sm text-text-secondary">Usuários Ativos</p>
                <p className="text-2xl font-bold tabular-nums text-text-title">
                  {metricsLoading ? '—' : metricsData?.activeUsers ?? 0}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4 rounded-lg border border-border p-4">
              <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-error/10 text-error">
                <AlertTriangle size={22} />
              </div>
              <div className="min-w-0">
                <p className="text-sm text-text-secondary">Erros Críticos</p>
                <p className="text-2xl font-bold tabular-nums text-error">
                  {metricsLoading ? '—' : metricsData?.criticalErrors ?? 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Online Devices List */}
        <div className="rounded-lg border border-border bg-card p-4 shadow-sm lg:p-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-text-title">
              Dispositivos Online ({onlineDevices.length})
            </h3>
            <div className="flex items-center gap-2">
              {isConnected ? (
                <>
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-online opacity-75"></span>
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-online"></span>
                  </span>
                  <span className="text-sm font-medium text-online">
                    Tempo Real Ativo
                  </span>
                </>
              ) : (
                <>
                  <span className="h-2.5 w-2.5 rounded-full bg-gray-400"></span>
                  <span className="text-sm font-medium text-text-hint">
                    Desconectado
                  </span>
                </>
              )}
            </div>
          </div>
          {devicesLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="size-8 animate-spin text-primary" />
            </div>
          ) : onlineDevices.length > 0 ? (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {onlineDevices.map((device) => (
                <button
                  key={device.id}
                  onClick={() => setSelectedDeviceId(device.id)}
                  aria-pressed={selectedDeviceId === device.id}
                  className={`flex flex-col gap-1.5 rounded-lg border p-4 text-left transition-all hover:shadow-card-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 active:scale-[0.99] ${
                    selectedDeviceId === device.id
                      ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                      : 'border-border bg-card hover:border-border-strong'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className="relative flex h-2.5 w-2.5">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-online opacity-75"></span>
                      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-online"></span>
                    </div>
                    <p className="truncate font-semibold text-text-title">{device.userName}</p>
                  </div>
                  <p className="truncate text-sm text-text-secondary">{device.model}</p>
                  <p className="text-xs text-text-secondary">
                    {device.platform === 'ios' ? 'iOS' : 'Android'} • v{device.appVersion}
                  </p>
                </button>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Smartphone size={48} className="mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium text-text-title">Nenhum dispositivo online</p>
                <p className="text-sm text-text-secondary">Aguardando dispositivos se conectarem</p>
              </div>
            </div>
          )}
        </div>

        {/* Show logs section */}
        {!selectedDevice ? (
          <div className="flex w-full items-center justify-center rounded-lg border border-border bg-card p-12 shadow-sm">
            <div className="text-center">
              <Smartphone size={48} className="mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium text-text-title">Selecione um dispositivo</p>
              <p className="mt-2 text-sm text-text-secondary">Escolha um dispositivo online para visualizar os logs em tempo real</p>
            </div>
          </div>
        ) : (
          <>
            {/* Selected Device Info */}
            <div className="rounded-lg border border-border bg-card p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="relative flex h-3 w-3">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-online opacity-75"></span>
                    <span className="relative inline-flex h-3 w-3 rounded-full bg-online"></span>
                  </div>
                  <div>
                    <p className="font-semibold text-text-title">{selectedDevice.userName}</p>
                    <p className="text-sm text-text-secondary">
                      {selectedDevice.model} • {selectedDevice.platform === 'ios' ? 'iOS' : 'Android'} {selectedDevice.osVersion}
                    </p>
                  </div>
                </div>
                <div className="text-right text-sm">
                  <p className="text-text-secondary">ID: <span className="font-mono text-text-title">{String(selectedDevice.id).slice(0, 13)}...</span></p>
                  <p className="text-text-secondary">App: v{selectedDevice.appVersion}</p>
                </div>
              </div>
            </div>

            {/* Log Controls Toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card p-3">
              <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filtrar por nível">
                {(['all', 'DEBUG', 'INFO', 'WARNING', 'ERROR', 'FATAL'] as const).map((level) => {
                  const active = selectedLevel === level;
                  return (
                    <button
                      key={level}
                      onClick={() => setSelectedLevel(level)}
                      aria-pressed={active}
                      className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                        active
                          ? 'bg-primary text-white shadow-sm'
                          : 'bg-muted text-text-secondary hover:bg-gray-200'
                      }`}
                    >
                      {level === 'all' ? 'Todos' : level}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setIsPaused(!isPaused)}
                  aria-label={isPaused ? 'Retomar stream' : 'Pausar stream'}
                  title={isPaused ? 'Retomar stream' : 'Pausar stream'}
                  className="flex size-9 items-center justify-center rounded-lg text-text-secondary transition-colors hover:bg-muted active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                >
                  {isPaused ? <Play size={18} /> : <Pause size={18} />}
                </button>
                <button
                  onClick={handleCopyAllLogs}
                  disabled={filteredLogs.length === 0}
                  aria-label="Copiar todos os logs"
                  className={`flex h-9 items-center justify-center gap-2 rounded-lg px-3 text-sm font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:cursor-not-allowed disabled:opacity-50 ${
                    copiedAll
                      ? 'bg-success text-white'
                      : 'border border-border-strong bg-card text-text-secondary hover:bg-muted/50'
                  }`}
                >
                  {copiedAll ? <Check size={16} /> : <Copy size={16} />}
                  <span className="hidden sm:inline">{copiedAll ? 'Copiado!' : 'Copiar'}</span>
                </button>
                <button
                  onClick={handleClearLogs}
                  aria-label="Limpar console"
                  title="Limpar console"
                  className="flex size-9 items-center justify-center rounded-lg text-text-secondary transition-colors hover:bg-muted active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                >
                  <Trash2 size={18} />
                </button>
                <button
                  onClick={handleExportLogs}
                  disabled={isExporting}
                  aria-label="Exportar logs"
                  title="Exportar logs"
                  className="flex size-9 items-center justify-center rounded-lg text-text-secondary transition-colors hover:bg-muted active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:opacity-50"
                >
                  {isExporting ? <RefreshCw size={18} className="animate-spin" /> : <Download size={18} />}
                </button>
              </div>
            </div>

            {/* Log Console */}
            <div className="flex h-[60dvh] min-h-[400px] flex-col rounded-lg bg-zinc-900 p-3 font-mono text-sm text-gray-200 shadow-card lg:h-[600px] lg:p-4">
              <div className="flex flex-shrink-0 items-center justify-between pb-3">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    {isConnected && !isPaused ? (
                      <>
                        <span className="relative flex h-2.5 w-2.5">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75"></span>
                          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500"></span>
                        </span>
                        <span className="text-xs font-bold uppercase tracking-wider text-red-400">LIVE</span>
                      </>
                    ) : isPaused ? (
                      <>
                        <span className="h-2.5 w-2.5 rounded-full bg-yellow-500"></span>
                        <span className="text-xs font-bold uppercase tracking-wider text-yellow-400">PAUSED</span>
                      </>
                    ) : (
                      <>
                        <span className="h-2.5 w-2.5 rounded-full bg-muted/500"></span>
                        <span className="text-xs font-bold uppercase tracking-wider text-text-hint">OFFLINE</span>
                      </>
                    )}
                  </div>
                  <span className="text-xs tabular-nums text-text-hint">
                    {filteredLogs.length} {selectedLevel !== 'all' ? selectedLevel : ''} {filteredLogs.length === 1 ? 'log' : 'logs'}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-300">Auto-scroll</span>
                  <label className="relative inline-flex cursor-pointer items-center">
                    <input
                      checked={autoScroll}
                      onChange={() => setAutoScroll(!autoScroll)}
                      className="peer sr-only"
                      type="checkbox"
                      aria-label="Alternar auto-scroll"
                    />
                    <div className={`peer h-6 w-11 rounded-full transition-colors after:absolute after:left-[2px] after:top-0.5 after:size-5 after:rounded-full after:bg-card after:shadow after:transition-transform after:content-[''] peer-checked:after:translate-x-5 peer-focus-visible:ring-2 peer-focus-visible:ring-white/30 ${autoScroll ? 'bg-primary' : 'bg-gray-700'}`} />
                  </label>
                </div>
              </div>
              <div ref={logContainerRef} className="flex-1 overflow-y-auto pr-2">
                {filteredLogs.length === 0 ? (
                  <div className="flex h-full items-center justify-center">
                    <div className="text-center">
                      <p className="text-text-hint">
                        {isPaused ? 'Stream pausado' : isConnected ? 'Aguardando logs...' : 'Desconectado do WebSocket'}
                      </p>
                      <p className="text-xs text-text-secondary mt-2">
                        {isConnected && !isPaused && 'Logs aparecerão aqui em tempo real'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1 text-[13px] leading-relaxed">
                    {filteredLogs.map((log) => {
                      const isApiError = log.metadata?.source === 'API' || log.deviceId === 'api-server';

                      const rowBg =
                        log.level === 'ERROR'
                          ? 'rounded-md bg-error/15 px-2 py-1'
                          : log.level === 'FATAL'
                          ? 'rounded-md bg-log-fatal/15 px-2 py-1'
                          : '';

                      return (
                        <p key={log.id} className={rowBg}>
                          <span className="tabular-nums text-text-secondary">{log.timestamp}</span>{' '}
                          <span className="font-bold" style={{ color: getLogColor(log.level) }}>
                            [{log.level}]
                          </span>{' '}
                          {isApiError && <span className="font-bold text-orange-400">[API] </span>}
                          <span className="text-text-hint">[{log.component}]</span>{' '}
                          {log.message}
                        </p>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
